import { FC, useState, useEffect } from 'react';
import { Lightbulb, Users, Trophy, Clock, Calendar, BookOpen, X, Users2,  ChevronRight, CheckCircle } from 'lucide-react';
import benjamin from '../assets/Benjamin.jpeg';
import SignUpForm from '../components/signupforms/SignUpForm';
import { useOnlineClasses } from '../contexts/OnlineClassContext';
import { OnlineClass } from '../types/api';
import FreeClassRegistrationModal from '../components/modals/FreeClassRegistrationModal';
import DatePreferenceModal from '../components/modals/DatePreferenceModal';
import { facebookPixelEvents } from '../utils/facebookPixel';
import { trackPageView } from '../utils/trackingUtils';
import { PAGE_TITLES } from '../constants/pageTracking';
import { usePageTracking } from '../hooks/usePageTracking';

// Constants
// const MOCK_CURRENT_DATE = new Date('2025-03-17T00:00:00.000Z'); // Mock current date for demo

interface ClassDetails {
  date: string;
  title: string;
  description: string;
  shortDescription: string;
  topics: string[];
  time: string;
  startDate: Date; // Adding actual Date object for calculations
  open: boolean;
  price?: number;
  currency?: string;
  maxStudents?: number;
  currentEnrollment?: number;
  instructorName?: string;
  id?: number;
  schedule?: any[]; // Add schedule information
}

// Utility functions
function formatPrice(price: number): string {
  return price.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

// Transform OnlineClass to ClassDetails for backward compatibility
const transformOnlineClassToClassDetails = (onlineClass: OnlineClass): ClassDetails => {
  const now = new Date();
  const isOpen = onlineClass.startDate > now && (
    !onlineClass.maxStudents || 
    !onlineClass.currentEnrollment || 
    onlineClass.currentEnrollment < onlineClass.maxStudents
  );

  // Use whatyourwilllearn if available, otherwise use default topics
  const topics = onlineClass.whatyourwilllearn && onlineClass.whatyourwilllearn.length > 0
    ? onlineClass.whatyourwilllearn
    : [
        "Launch and run your own virtual business company",
        "Master the art of spotting profitable opportunities", 
        "Learn the secrets of successful business strategy",
        "Compete with other teen entrepreneurs in real-time"
      ];

  return {
    date: formatDateString(onlineClass.startDate),
    title: onlineClass.title,
    description: onlineClass.description,
    shortDescription: onlineClass.shortDescription,
    topics: topics,
    time: formatTimeString(onlineClass.startDate, onlineClass.endDate),
    startDate: onlineClass.startDate,
    open: isOpen,
    price: onlineClass.price,
    currency: onlineClass.currency,
    maxStudents: onlineClass.maxStudents,
    currentEnrollment: onlineClass.currentEnrollment,
    instructorName: onlineClass.instructorName,
    id: onlineClass.id,
    schedule: onlineClass.schedule || [] // Include schedule data
  };
};

// Helper functions for date/time formatting
const formatDateString = (date: Date): string => {
  const now = new Date();
  if (date < now) {
    return "Past Event";
  }
  
  const options: Intl.DateTimeFormatOptions = { 
    month: 'long', 
    day: 'numeric'
  };
  return date.toLocaleDateString('en-US', options);
};

const formatTimeString = (startDate: Date, endDate?: Date): string => {
  const dayName = startDate.toLocaleDateString('en-US', { weekday: 'long' });
  const startTime = startDate.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
  
  if (endDate) {
    const endTime = endDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    return `${dayName}, ${startTime} - ${endTime} EST`;
  }
  
  return `${dayName}, ${startTime} EST`;
};

// No fallback data - show empty state when API fails

// Separate Components
// const CountdownTimer: React.FC<{ endDate: Date }> = ({ endDate }) => {
//   const [timeLeft, setTimeLeft] = useState('');

//   useEffect(() => {
//     const calculateTimeLeft = () => {
//       const now = MOCK_CURRENT_DATE;
//       const difference = endDate.getTime() - now.getTime();

//       if (difference <= 0) {
//         setTimeLeft('Offer expired');
//         return;
//       }

//       const days = Math.floor(difference / (1000 * 60 * 60 * 24));
//       const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
//       setTimeLeft(`${days}d ${hours}h remaining`);
//     };

//     calculateTimeLeft();
//     const timer = setInterval(calculateTimeLeft, 1000 * 60); // Update every minute

//     return () => clearInterval(timer);
//   }, [endDate]);

//   return (
//     <div className="text-sm font-medium text-yellow-500">
//       {timeLeft}
//     </div>
//   );
// };

// const PricingCard: FC<{ classDetails: ClassDetails }> = ({ classDetails }) => {
//   const basePrice = classDetails.price || 99;
//   const isFree = classDetails.price === 0;

//   return (
//     <div className="bg-gray-50 rounded-lg p-4 space-y-4">
//       <div className="flex justify-between items-baseline">
//         <span className="font-medium">{isFree ? "Class Price" : "Standard Price"}</span>
//         <div className="flex items-center">
//           <span className="text-2xl font-bold text-gray-900">
//             {isFree ? "FREE" : formatPrice(basePrice/100)}
//           </span>
//           {isFree && (
//             <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium ml-2 animate-pulse">
//               FREE
//             </span>
//           )}
//         </div>
//       </div>

//       {/* Secure Your Spot Button - moved to top */}
//       {classDetails.date !== "Coming Soon" && classDetails.date !== "Past Event" && (
//         <div className="pt-3">
//           <a 
//             href={classDetails?.id ? `https://platform.emploskills.com/online-class/${classDetails.id}/preview` : `https://platform.emploskills.com/`}
//             target="_blank"
//             rel="noopener noreferrer"
//             className="block w-full py-3 px-6 text-center bg-primary text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
//           >
//             Secure Your Spot Now
//           </a>
//         </div>
//       )}




//     </div>
//   );
// };


const ClassModal: FC<{ 
  isOpen: boolean; 
  onClose: () => void; 
  classDetails: ClassDetails | null;
}> = ({ isOpen, onClose, classDetails }) => {
  if (!isOpen || !classDetails) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">{classDetails.title}</h3>
              <p className="text-blue-600 font-medium text-lg">{classDetails.date}</p>
              {classDetails.time && (
                <p className="text-gray-600 mt-1">{classDetails.time}</p>
              )}
            </div>
            <button 
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>
          
          {/* Class Description */}
          <div className="mb-6">
            <p className="text-gray-700 text-lg leading-relaxed">{classDetails.description}</p>
          </div>

          {/* Class Schedule */}
          {classDetails.schedule && classDetails.schedule.length > 0 && (
            <div className="mb-6">
              <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-blue-500" />
                Class Schedule
              </h4>
              <div className="space-y-3">
                {classDetails.schedule.map((scheduleItem: any, index: number) => {
                  // Parse the schedule date and times
                  const scheduleDate = new Date(scheduleItem.date);
                  const formatDate = scheduleDate.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  });
                  
                  // Format times
                  const startTime = scheduleItem.startTime.substring(0, 5); // Remove seconds
                  const endTime = scheduleItem.endTime.substring(0, 5); // Remove seconds
                  
                  return (
                    <div key={index} className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-blue-900">{formatDate}</p>
                          <p className="text-blue-700">
                            {startTime} - {endTime} ({scheduleItem.timeZone})
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Session {index + 1}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Class Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Left Column - Class Info */}
            <div className="space-y-4">
              {/* Instructor */}
              {classDetails.instructorName && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <Users className="w-4 h-4 mr-2 text-blue-500" />
                    Instructor
                  </h4>
                  <p className="text-gray-700">{classDetails.instructorName}</p>
                </div>
              )}

              {/* Class Capacity */}
              {classDetails.maxStudents && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <Users className="w-4 h-4 mr-2 text-blue-500" />
                    Class Size
                  </h4>
                  <p className="text-gray-700">
                    Maximum {classDetails.maxStudents} students
                  </p>
                </div>
              )}

              {/* Class Status */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-blue-500" />
                  Status
                </h4>
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-2 ${
                    classDetails.open ? 'bg-green-500' : 'bg-gray-400'
                  }`}></div>
                  <span className={`font-medium ${
                    classDetails.open ? 'text-green-700' : 'text-gray-600'
                  }`}>
                    {classDetails.open ? 'Open for Registration' : 'Registration Closed'}
                  </span>
                </div>
              </div>
            </div>

            {/* Right Column - Topics */}
            <div>
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <BookOpen className="w-4 h-4 mr-2 text-blue-500" />
                  What You'll Learn
                </h4>
                <ul className="space-y-2">
                  {classDetails.topics.map((topic, index) => (
                    <li key={index} className="flex items-start text-gray-700">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                      <span className="text-sm">{topic}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Pricing and Registration */}
          {classDetails.date !== "Coming Soon" && classDetails.date !== "Past Event" && (
            <div className="border-t pt-6">
              {/* <div className="space-y-4 mb-6">
                <div>
                  <p className="text-xl font-semibold text-gray-900">Ready to join?</p>
                  <p className="text-gray-600">Secure your spot in this {classDetails.open ? 'available' : 'upcoming'} class</p>
                </div>
                <PricingCard classDetails={classDetails} />
              </div> */}
                    {/* Join Community Section - moved to bottom */}
      {classDetails.date !== "Coming Soon" && classDetails.date !== "Past Event" && (
        <div className="bg-white rounded-lg p-4 text-center">
          <div className="mb-2">
            <h4 className="font-semibold">Join our Community</h4>
            {/* <span className="bg-green-500 text-white text-sm px-2 py-1 rounded">Save 40%</span> */}
          </div>
          <p className="text-sm mb-2">Join our Facebook community to receive updates and news about our classes!</p>
          <a 
            href="https://www.facebook.com/groups/1163931521921469" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center text-white bg-blue-600 hover:bg-blue-700 transition-colors px-4 py-2 rounded-lg mt-2"
          >
            <span>Join Community</span>
            <ChevronRight className="w-4 h-4 ml-1" />
          </a>
          {/* <p className="text-xl font-bold mt-4 text-green-600">Only $59 with community code</p>
          <p className="text-sm mt-1 text-gray-600">Plus exclusive access to future discounts!</p> */}
        </div>
      )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Add InstructorModal component
const InstructorModal: FC<{ 
  isOpen: boolean; 
  onClose: () => void; 
}> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Meet Benjamin Ryan – Your VBC Online Mentor</h3>
              <p className="text-blue-600 font-medium">Co-Founder and Head of Sales EmploSkills</p>
            </div>
            <button 
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>
          
          <div className="mb-6 flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-1/3">
              <div className="h-64 overflow-hidden rounded-xl border-2 border-gray-100 shadow-sm">
                <img
                  src={benjamin}
                  alt="Benjamin Ryan"
                  className="w-full h-full object-cover object-[center_18%] scale-110"
                />
              </div>
            </div>
            
            <div className="w-full md:w-2/3">
              <p className="text-gray-700 mb-4">
                Benjamin Ryan is a 24-year-old entrepreneur, Co-Founder of EmploSkills, and an expert in simulation-based learning. His journey began as a secondary school student when he first used Hubro Simulations in class.
              </p>
              
              <p className="text-gray-700 mb-4">
                That early hands-on experience ignited his passion for business, entrepreneurship, and financial literacy. At just 17, Benjamin landed an internship with Hubro Simulations, and by 18, he was managing sales and supporting students across the world. Now, with over 8 years of experience delivering business simulations to thousands of learners globally, Benjamin brings a Gen Z perspective to teaching. He's dedicated to helping teens step out of their comfort zones and develop real-world skills through social, practical learning.
              </p>
              
              <p className="text-gray-700 mb-4">
                Benjamin understands today's students because he still walks in their shoes. His mentorship focuses on empowering the next generation through engaging, skills-based, and future-ready education.
              </p>
            </div>
          </div>
          
          <div className="border-t pt-4">
            <h4 className="font-semibold text-lg mb-3">Why Students Love My Classes</h4>
            <ul className="space-y-1">
              <li className="flex items-center text-gray-700">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                Engaging real-world application of business concepts and skills
              </li>
              <li className="flex items-center text-gray-700">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                Actionable insights teens can use right away
              </li>
              <li className="flex items-center text-gray-700">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                Relatable examples and interactive coaching style
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add GroupQuoteModal component
const GroupQuoteModal: FC<{ 
  isOpen: boolean; 
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Request Group Quote for VBC Online</h3>
              <p className="text-gray-600">Get special pricing for your group or co-op</p>
            </div>
            <button 
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>
          <SignUpForm onSuccess={onClose} />
        </div>
      </div>
    </div>
  );
};

const VBCOnline: FC = () => {
  const [selectedClass, setSelectedClass] = useState<ClassDetails | null>(null);
  const [showInstructorModal, setShowInstructorModal] = useState(false);
  const [showGroupQuoteModal, setShowGroupQuoteModal] = useState(false);
  const [showFreeClassModal, setShowFreeClassModal] = useState(false);
  const [showDatePreferenceModal, setShowDatePreferenceModal] = useState(false);
  
  // Use page tracking hook
  usePageTracking();
  
  // Use the online class context - upcomingClasses is already filtered to show only featured classes
  const { upcomingClasses, loading, error, refreshOnlineClasses } = useOnlineClasses();

  // Transform API data to ClassDetails format - don't use fallback data
  // Note: upcomingClasses already contains only featured classes (filtered in context)
  const classData: ClassDetails[] = upcomingClasses.length > 0
    ? upcomingClasses.map(transformOnlineClassToClassDetails)
    : [];

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Track page view
    trackPageView('VBC_ONLINE', 'vbc-online');

    // Track Facebook Pixel page view
    facebookPixelEvents.viewContent({
      content_name: PAGE_TITLES.VBC_ONLINE.tracking,
      content_category: 'vbc_online',
      content_type: 'online_classes'
    });
  }, []);

  // Add section view tracking
  useEffect(() => {
    const sections = [
      { id: 'hero-section', name: 'Hero Section' },
      { id: 'free-class-promotion', name: 'Free Class Promotion' },
      { id: 'course-details', name: 'Course Details' },
      { id: 'group-rates-section', name: 'Group Rates Section' },
      { id: 'parents-info-section', name: 'Parents Info Section' }
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const sectionName = sections.find(s => s.id === entry.target.id)?.name;
            if (sectionName) {
              // Track section view
              if (typeof window.gtag !== 'undefined') {
                window.gtag('event', 'section_view', {
                  page_title: PAGE_TITLES.VBC_ONLINE.tracking,
                  section_name: sectionName
                });
              }

              // Track with Facebook Pixel
              facebookPixelEvents.viewContent({
                content_name: `${PAGE_TITLES.VBC_ONLINE.tracking} - ${sectionName}`,
                content_category: 'page_section',
                content_type: 'section_view'
              });
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    sections.forEach(section => {
      const element = document.getElementById(section.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  // Loading state
  if (loading && upcomingClasses.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading online classes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Error notification */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mx-4 mt-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <X className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                {error}. Showing fallback data.
                <button 
                  onClick={refreshOnlineClasses}
                  className="ml-2 text-red-800 underline hover:text-red-900"
                >
                  Try again
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section id="hero-section" className="px-4 py-20 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
            VBC Online
          </h1>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl md:text-4xl">Live Business Classes</h2>
          <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
            Join our live, interactive business simulation where teens run virtual companies, compete with peers, and learn real-world business skills!
          </p>
          <h2 className="mt-12 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl md:text-4xl">Why VBC Online?</h2>
        </div>
        <div className="text-center mb-12">
          <p className="mt-4 max-w-2xl mx-auto text-lg text-gray-600">
            Watch how we can engage your teen in our live business simulation and teach them real-world business skills!
          </p>
        </div>
        
                 <div className="max-w-4xl mx-auto">
           <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
             <div className="aspect-video">
               <iframe
                 src="https://www.youtube.com/embed/sCahXnrMqKQ?rel=0&modestbranding=1"
                 title="VBC Online Business Simulation"
                 frameBorder="0"
                 allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                 allowFullScreen
                 className="w-full h-full border-0"
                 loading="lazy"
               ></iframe>
             </div>
           </div>
          
        </div>
        {/* Value Props */}
        <div className="mt-20 grid grid-cols-1 gap-8 sm:grid-cols-2">
          <div className="relative p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <div className="bg-blue-500 p-3 rounded-full">
                <Lightbulb className="w-6 h-6 text-white" />
              </div>
            </div>
            <h3 className="mt-4 text-xl font-semibold text-gray-900">Simple & Engaging</h3>
            <p className="mt-2 text-gray-600">Business concepts are made real, engaging, and fun! VBC Online introduces finance, budgeting, accounting, marketing, entrepreneurship, and strategy in a simple, gamified way that homeschooling teens will love.</p>
          </div>
          
          <div className="relative p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <div className="bg-green-500 p-3 rounded-full">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
            <h3 className="mt-4 text-xl font-semibold text-gray-900">Learn by Doing</h3>
            <p className="mt-2 text-gray-600">Students take on the role of business owners of a Jetpack production company in an interactive, competitive simulation by Hubro Simulations. They'll solve real-world business challenges and apply business concepts in a hands-on environment.</p>
          </div>
          
          <div className="relative p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <div className="bg-green-500 p-3 rounded-full">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
            <h3 className="mt-4 text-xl font-semibold text-gray-900">Social Learning</h3>
            <p className="mt-2 text-gray-600">Your teen will collaborate with other homeschooling students, compete in teams, and develop critical social and business, and leadership skills.</p>
          </div>
          
          <div className="relative p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <div className="bg-purple-500 p-3 rounded-full">
                <Trophy className="w-6 h-6 text-white" />
              </div>
            </div>
            <h3 className="mt-4 text-xl font-semibold text-gray-900">Competitive Edge</h3>
            <p className="mt-2 text-gray-600">Inspire motivation and engagement! Students compete for the top spot in a friendly business competition, learning while having fun.</p>
          </div>
        </div>
      </section>

      {/* Free Class Promotion Section - Only show if there are free classes available */}
      {classData.filter(cls => cls.price === 0 && cls.open).length > 0 && (
        <section id="free-class-promotion" className="py-16 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="relative bg-gradient-to-br from-green-500 to-emerald-600 px-8 py-12">
              <div className="absolute top-4 right-4">
                <span className="bg-white text-green-600 text-sm font-bold px-4 py-2 rounded-full animate-pulse shadow-lg">
                  🎉 FREE CLASS
                </span>
              </div>
              <div className="text-center text-white">
                <h2 className="text-4xl font-bold mb-4">Try VBC Online FREE!</h2>
                <h3 className="text-2xl font-bold mb-4">See the Method in Action—Before You Commit</h3>
                <p className="text-xl text-green-50 max-w-2xl mx-auto">
                Our free VBC online business class gives teens aged 13–18 a risk-free way to experience our teaching approach, see how the simulation works, and decide if it fits your teen’s learning style.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-8">
              {/* Instructor Spotlight */}
              <div className="lg:col-span-1">
                <div className="text-center">
                  <div className="relative mx-auto w-32 h-32 mb-4">
                    <div className="w-32 h-32 overflow-hidden rounded-full border-4 border-green-200 shadow-lg">
                      <img
                        src={benjamin}
                        alt="Benjamin Ryan"
                        className="w-full h-full object-cover object-[center_18%] scale-110"
                      />
                    </div>
                    <div className="absolute -bottom-2 -right-2 bg-green-500 text-white p-2 rounded-full shadow-lg">
                      <Users className="w-4 h-4" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Meet Benjamin Ryan</h3>
                  <p className="text-green-600 font-medium mb-3">Your VBC Online Instructor</p>
                  <p className="text-gray-600 text-sm">
                    Gen Z entrepreneur with 8+ years of experience teaching business simulations to thousands of students worldwide.
                  </p>
                  <button 
                    onClick={async () => {
                      setShowInstructorModal(true);
                      // Track instructor modal view
                      facebookPixelEvents.viewContent({
                        content_name: 'Benjamin Ryan - Instructor Bio',
                        content_category: 'instructor_bio',
                        content_type: 'modal_view'
                      });
                    }}
                    className="mt-4 text-green-600 hover:text-green-700 font-medium flex items-center justify-center mx-auto"
                  >
                    Learn more about Benjamin
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>

              {/* Free Class Details */}
              <div className="lg:col-span-2">
                <div className="space-y-6">
                  <div>
                    {/* <h3 className="text-2xl font-bold text-gray-900 mb-4">What's Included in Your FREE Class</h3> */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-start space-x-3">
                        <div className="bg-green-100 p-2 rounded-lg">
                          <Trophy className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Start Your Simulation-Based Learning </p>
                          <p className="text-sm text-gray-600">Step into the world of business education designed for future-ready teens aged 13–18.</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="bg-green-100 p-2 rounded-lg">
                          <Users className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Explore Risk-Free </p>
                          <p className="text-sm text-gray-600">Join a 3-hour test class with no commitment and unlock exclusive learning benefits after class!</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="bg-green-100 p-2 rounded-lg">
                          <BookOpen className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Run a Virtual Company</p>
                          <p className="text-sm text-gray-600">Experience real-world decision-making that mirrors real business challenges.</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="bg-green-100 p-2 rounded-lg">
                          <Clock className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Guided by Experts </p>
                          <p className="text-sm text-gray-600">Learn from Benjamin and Daniel—experienced mentors who connect and coach with impact.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Free Class Schedule */}
                  <div className="bg-green-50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Calendar className="w-5 h-5 mr-2 text-green-600" />
                      Upcoming FREE Classes
                    </h4>
                    {loading ? (
                      <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500 mx-auto mb-2"></div>
                        <p className="text-sm text-gray-500">Loading free classes...</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {classData.filter(cls => cls.price === 0 && cls.open).length > 0 ? (
                          classData.filter(cls => cls.price === 0 && cls.open).map((freeClass, index) => (
                            <div 
                              key={freeClass.id || index}
                              className="bg-white rounded-lg p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                              onClick={async () => {
                                setSelectedClass(freeClass);
                                // Track free class detail view
                                facebookPixelEvents.viewContent({
                                  content_name: `${freeClass.title} - Free Class Details`,
                                  content_category: 'free_class_details',
                                  content_type: 'class_detail'
                                });
                              }}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <h5 className="font-semibold text-gray-900">{freeClass.title}</h5>
                                <span className="bg-green-500 text-white text-xs px-3 py-1 rounded-full font-medium animate-pulse">
                                  FREE
                                </span>
                              </div>
                              <p className="text-green-600 font-medium">{freeClass.date}</p>
                              <p className="text-gray-600 text-sm">{freeClass.time}</p>
                              <p className="text-gray-700 text-sm mt-2">{freeClass.shortDescription}</p>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-6">
                            <p className="text-gray-600 mb-4">No free classes currently scheduled</p>
                            <p className="text-sm text-gray-500">Check back soon or join our community for updates!</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    {classData.filter(cls => cls.price === 0 && cls.open).length > 0 ? (
                      <button
                        onClick={async () => {
                          setShowFreeClassModal(true);
                          // Track free class signup click
                          facebookPixelEvents.lead({
                            content_name: 'Free Class Signup',
                            content_category: 'free_class',
                            content_type: 'signup_click'
                          });
                        }}
                        className="flex-1 bg-green-600 text-white text-center py-4 px-6 rounded-xl font-semibold hover:bg-green-700 transition-colors text-lg shadow-lg"
                      >
                        🎯 Sign up now for FREE
                      </button>
                    ) : (
                      <a 
                        href="https://www.facebook.com/groups/1163931521921469"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={async () => {
                          // Track community join click
                          facebookPixelEvents.lead({
                            content_name: 'Community Join - Facebook Group',
                            content_category: 'community',
                            content_type: 'external_link'
                          });
                        }}
                        className="flex-1 bg-green-600 text-white text-center py-4 px-6 rounded-xl font-semibold hover:bg-green-700 transition-colors text-lg shadow-lg"
                      >
                        🔔 Get Notified of FREE Classes
                      </a>
                    )}
                    <button
                      onClick={async () => {
                        setShowDatePreferenceModal(true);
                        // Track date preference click
                        facebookPixelEvents.lead({
                          content_name: 'Date Preference Form',
                          content_category: 'form_interaction',
                          content_type: 'date_preference'
                        });
                      }}
                      className="flex-1 bg-blue-600 text-white text-center py-4 px-6 rounded-xl font-semibold hover:bg-blue-700 transition-colors text-lg"
                    >
                      💬 Not sure about the date? Show interest
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Banner */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-8 py-6">
              <div className="text-center text-white">
                <p className="text-lg font-medium mb-2">🌟 Limited Time Offer - No Credit Card Required</p>
                <p className="text-green-100">Experience the full VBC Online program absolutely free. Perfect way to see if it's right for your teen!</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Course Details Grid */}
      <section id="course-details" className="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Instructor Column */}
          <div className="bg-white rounded-2xl shadow-lg flex flex-col h-full relative pt-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center px-8">Meet Your Instructor</h3>
            <div className="relative w-full px-4 mb-6">
              <div className="h-60 mx-auto overflow-hidden rounded-xl border-2 border-gray-100 shadow-sm">
                <img
                  src={benjamin}
                  alt="Benjamin Ryan"
                  className="w-full h-full object-cover object-[center_18%] scale-110"
                />
              </div>
            </div>
            <div className="flex-grow px-8">
              <h4 className="font-semibold text-lg mb-2">Benjamin Ryan</h4>
              <p className="text-blue-600 text-sm mb-4">Co-Founder and Head of Sales EmploSkills</p>
              <p className="text-gray-600 mb-6 line-clamp-3">
              I am a Gen Z entrepreneur and simulation-based learning expert who helps teens turn business theory into real-world action.
              </p>
              <button 
                onClick={() => setShowInstructorModal(true)}
                className="flex items-center text-blue-600 font-medium hover:text-blue-800 transition-colors"
              >
                Read more about me
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
              <div className="border-t mt-4 pt-3">
                <h5 className="font-medium mb-2">Why Students Love My Classes</h5>
                <ul className="space-y-1">
                  <li className="flex items-center text-sm text-gray-600">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                    Engaging real-world application
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                    Actionable insights
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                    Interactive coaching style
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Sign Up Column */}
          <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl shadow-lg p-8 text-white flex flex-col h-full">
            <h3 className="text-2xl font-bold mb-6">Standard Pricing & Enrollment</h3>
            <div className="space-y-6 mb-8 flex-grow">
              {/* Standard Pricing */}
              <div>

                <p className="text-2xl font-bold mb-4">$119</p>

                <ul className="space-y-2 text-sm text-white/90">
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-white/80 rounded-full mr-2 flex-shrink-0"></div>
                    6-hour Instructor-Led Virtual Business Class, using Hubro's award-winning business simulations
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-white/80 rounded-full mr-2 flex-shrink-0"></div>
                    Live team-based competition with other homeschooling teens
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-white/80 rounded-full mr-2 flex-shrink-0"></div>
                    Certificate of Completion for your teen
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-white/80 rounded-full mr-2 flex-shrink-0"></div>
                    Access to learning materials (videos, readings, training simulation) before class
                  </li>
                  <li className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-white/80 rounded-full mr-2 flex-shrink-0"></div>
                    Access to 3 selected Business Labs and Simulations with exercises, content and dilemmas after the class
                  </li>
                  <br/>
                  <p className="text-sm mt-1 text-white/80">Join Our Community and get a 20% Discount on a class</p>
                </ul>
              </div>

              {/* Community Discount */}
              {/* <div className="bg-white/10 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold">Join our Community</h4>
                  <span className="bg-green-500 text-white text-sm px-2 py-1 rounded">Save 40%</span> 
                </div>
                <p className="text-sm mb-2">Join our Facebook community to receive updates and news about our classes!</p>
                <a 
                  href="https://www.facebook.com/groups/1163931521921469" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-white bg-blue-600 hover:bg-blue-700 transition-colors px-4 py-2 rounded-lg mt-2"
                >
                  <span>Join Community</span>
                  <ChevronRight className="w-4 h-4 ml-1" />
                </a>
                <p className="text-xl font-bold mt-4">Only $59 with community code</p>
                <p className="text-sm mt-1 text-white/80">Plus exclusive access to future discounts!</p>
              </div> */}

              {/* Group Discounts */}
              {/* <div className="space-y-4">
                <h4 className="text-xl font-semibold">Group & Team Discounts</h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <Users2 className="w-5 h-5 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-medium">Homeschool Co-Op & Group Rates</p>
                      <p className="text-sm">Special pricing for groups of five or more</p>
                    </div>
                  </div>
                </div>
              </div> */}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="https://platform.emploskills.com/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={async () => {
                  // Track platform exploration click
                  facebookPixelEvents.lead({
                    content_name: 'Platform Library Exploration',
                    content_category: 'platform_navigation',
                    content_type: 'external_link'
                  });
                }}
                className="block flex-1 py-3 px-6 text-center bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Explore our Library
              </a>
              {/* <button
                onClick={() => setShowGroupQuoteModal(true)}
                className="block flex-1 py-3 px-6 text-center bg-blue-700 text-white rounded-lg font-semibold hover:bg-blue-800 border border-white/20 transition-colors"
              >
                Request Group Quote
              </button> */}
            </div>
          </div>

          {/* When & How Column */}
          <div className="bg-white rounded-2xl shadow-lg p-8 flex flex-col h-full">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">What and When</h3>
            <div className="space-y-6 flex-grow">
              <div className="flex items-start">
                <Calendar className="w-6 h-6 text-blue-500 mr-4 mt-1 flex-shrink-0" />
                <div className="flex-grow">
                  <h4 className="font-semibold">Upcoming Classes</h4>
                  {loading ? (
                    <div className="mt-2 text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-2"></div>
                      <p className="text-sm text-gray-500">Loading classes...</p>
                    </div>
                  ) : classData.length === 0 ? (
                    <div className="mt-2 py-4">
                      <p className="text-gray-500">No upcoming classes</p>
                    </div>
                  ) : (
                    <div className="mt-2 space-y-3">
                      {classData.map((classItem, index) => (
                        <div 
                          key={classItem.id || index}
                          className={`p-4 rounded-lg cursor-pointer transition-colors ${
                            classItem.open === false 
                              ? 'bg-gray-50 hover:bg-gray-100' 
                              : 'bg-blue-50 hover:bg-blue-100'
                          }`}
                          onClick={async () => {
                            setSelectedClass(classItem);
                            // Track class detail view
                            facebookPixelEvents.viewContent({
                              content_name: `${classItem.title} - Class Details`,
                              content_category: classItem.price === 0 ? 'free_class' : 'paid_class',
                              content_type: 'class_detail',
                              value: classItem.price ? classItem.price / 100 : undefined,
                              currency: classItem.currency || 'USD'
                            });
                          }}
                        >
                          <div className="flex items-start justify-between mb-1">
                            <p className={`font-medium text-lg ${
                              classItem.open === false ? 'text-gray-600' : 'text-blue-900'
                            }`}>
                              {classItem.title}
                            </p>
                            {classItem.price === 0 && (
                              <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium ml-2 flex-shrink-0 animate-pulse">
                                FREE
                              </span>
                            )}
                          </div>
                          <p className={`text-sm mb-1 ${
                            classItem.open === false ? 'text-gray-500' : 'text-blue-700'
                          }`}>
                            {classItem.date}
                          </p>
                          <p className="text-gray-600 text-sm mb-2">{classItem.time}</p>
                          <p className="text-gray-600 text-sm mb-2">{classItem.shortDescription}</p>
                          
                          {/* Price and Enrollment Info */}
                          <div className="flex items-center justify-between mt-2">
                            <div className="text-sm">
                              {classItem.price === 0 ? (
                                <span className="text-green-600 font-semibold">Free Class</span>
                              ) : classItem.price ? (
                                <span className="text-blue-600 font-semibold">
                                  {formatPrice(classItem.price / 100)}
                                </span>
                              ) : (
                                <span className="text-gray-500 font-semibold">Price TBA</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
             
            </div>
          </div>
        </div>
      </section>

      {/* Co-Op & Micro-Schools Section */}
      <section id="group-rates-section" className="py-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Group Rates for Homeschool Co-Ops & Micro-Schools
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                If you're part of a homeschool co-op or micro-school you can take advantage of our exclusive group rates when enrolling five or more students. Our special pricing options make it even more affordable to bring VBC Online to your learning community.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Empower your home schooling students and enrich your curriculum with hands-on, real-world business skills and an entrepreneurial mindset—all tailored to fit your unique homeschooling needs.
              </p>
              <div className="bg-white rounded-xl shadow-md p-6 mb-8">
                <h3 className="text-xl font-semibold mb-4">Benefits for Co-Ops & Micro-Schools:</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="bg-blue-100 p-2 rounded-lg mr-4">
                      <Users2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium">Group Discounts</h4>
                      <p className="text-gray-600">Special rates for groups of 5 or more students</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-green-100 p-2 rounded-lg mr-4">
                      <BookOpen className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-medium">Customized Learning</h4>
                      <p className="text-gray-600">Programs tailored to your curriculum needs</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-purple-100 p-2 rounded-lg mr-4">
                      <Trophy className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-medium">Enhanced Collaboration</h4>
                      <p className="text-gray-600">Students learn and compete together as a group</p>
                    </div>
                  </li>
                </ul>
              </div>
              {/* <button
                onClick={() => setShowGroupQuoteModal(true)}
                className="inline-flex items-center bg-primary text-white px-8 py-4 rounded-full font-semibold hover:bg-blue-700 transition-colors text-lg"
              >
                Request a Quote Today
                <ChevronRight className="w-5 h-5 ml-2" />
              </button> */}
            </div>
            <div className="relative">
              <div className="absolute -inset-4">
                <div className="w-full h-full mx-auto rotate-2 bg-gradient-to-r from-blue-500 to-blue-600 rounded-3xl opacity-30 blur-xl"></div>
              </div>
              <div className="relative">
                <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
                  <div className="flex items-center justify-between border-b border-gray-100 pb-6">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">Group Package</h3>
                      <p className="text-gray-500">For Co-Ops & Micro-Schools</p>
                    </div>
                    <div className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full font-medium">
                      5+ Students
                    </div>
                  </div>
                  <ul className="space-y-4">
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                      <span>Customized group pricing</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                      <span>Dedicated support for coordinators</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                      <span>Flexible scheduling options</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                      <span>Group progress tracking</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                      <span>Curriculum integration support</span>
                    </li>
                  </ul>
                  <div className="border-t border-gray-100 pt-6">
                    <p className="text-center text-gray-600 mb-4">Contact us to discuss your group's needs</p>
                    <button
                      onClick={async () => {
                        setShowGroupQuoteModal(true);
                        // Track group quote request
                        facebookPixelEvents.lead({
                          content_name: 'Group Quote Request',
                          content_category: 'group_sales',
                          content_type: 'quote_request'
                        });
                      }}
                      className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                    >
                      Get Custom Quote
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Parents Info Section */}
      <section id="parents-info-section" className="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8 bg-blue-50">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
            What Parents Need to Know
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Are you looking for engaging, high-quality educational experiences that prepare teens for the future?
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          <p className="text-lg text-gray-700 mb-8">
            VBC Online is designed to:
          </p>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <Trophy className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-gray-700">
                  Provide a structured, interactive learning experience that blends competition with real-world business skills.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-gray-700">
                  Encourage teamwork, leadership, and decision-making in a way that's fun and engaging.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-gray-700">
                  Be a low-commitment, high-impact program—just 6 hours of live instruction with no extra prep required from parents.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-gray-700">
                  Offer recognition for participation—students receive a Certificate of Completion to showcase their learning.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 p-6 bg-blue-50 rounded-xl">
            <p className="text-lg text-blue-900 font-medium text-center">
              A hands-on, practical way to introduce business education—without adding more to your plate.
            </p>
          </div>
        </div>
      </section>

      <ClassModal 
        isOpen={selectedClass !== null}
        onClose={() => setSelectedClass(null)}
        classDetails={selectedClass}
      />
      
      <InstructorModal
        isOpen={showInstructorModal}
        onClose={() => setShowInstructorModal(false)}
      />

      <GroupQuoteModal
        isOpen={showGroupQuoteModal}
        onClose={() => setShowGroupQuoteModal(false)}
      />

      <FreeClassRegistrationModal
        isOpen={showFreeClassModal}
        onClose={() => setShowFreeClassModal(false)}
        classDetails={
          classData.find(cls => cls.price === 0 && cls.open) 
            ? {
                title: classData.find(cls => cls.price === 0 && cls.open)!.title,
                date: classData.find(cls => cls.price === 0 && cls.open)!.date,
                time: classData.find(cls => cls.price === 0 && cls.open)!.time,
                id: classData.find(cls => cls.price === 0 && cls.open)!.id
              }
            : undefined
        }
      />

      <DatePreferenceModal
        isOpen={showDatePreferenceModal}
        onClose={() => setShowDatePreferenceModal(false)}
      />
    </div>
  );
};

export default VBCOnline; 